import React, {useRef, useState} from 'react';

import ReactCrop, {centerCrop,makeAspectCrop} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {useTranslation} from "react-i18next";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import {v4 as uuidv4} from "uuid";



/**
 * Enhanced image cropping function optimized for mobile devices
 * Key improvements:
 * 1. Uses natural image dimensions to maintain high resolution
 * 2. Handles high-DPI displays (common on mobile)
 * 3. Enables high-quality image smoothing
 * 4. Automatically selects best format (PNG for quality, JPEG for size)
 */
function getCroppedImg(image, crop, fileName) {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    // Use natural dimensions to maintain high resolution
    // This is crucial for mobile where display dimensions might be small
    const cropWidth = crop.width * scaleX;
    const cropHeight = crop.height * scaleY;
    
    // Handle high-DPI displays (common on mobile devices)
    // This ensures crisp images on retina displays
    const pixelRatio = window.devicePixelRatio || 1;
    
    // Set canvas to the actual crop dimensions (not scaled down)
    canvas.width = cropWidth * pixelRatio;
    canvas.height = cropHeight * pixelRatio;
    
    // Scale the canvas context to match the device pixel ratio
    const ctx = canvas.getContext('2d');
    ctx.scale(pixelRatio, pixelRatio);
    
    // Enable high-quality image rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight,
    );

    // Determine best format based on image characteristics
    // Use PNG for images with transparency or when quality is critical
    const hasTransparency = cropWidth > 0 && cropHeight > 0;
    const format = hasTransparency ? 'image/png' : 'image/jpeg';
    const quality = format === 'image/jpeg' ? 0.95 : undefined;

    // Return as blob with maximum quality
    return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
            blob.name = fileName;
            resolve(blob);
        }, format, quality);
    });
}

const CropImages = ({handleCancel, handleApprove, isOpen, src}) => {
    const [crop, setCrop] = useState()
    const [completedCrop, setCompletedCrop] = useState(null);
    const imageRef = useRef();
    const onImageLoad=(e)=> {
        const { naturalWidth: width, naturalHeight: height } = e.currentTarget
      
        const crop = centerCrop(
          makeAspectCrop(
            {
              // You don't need to pass a complete crop into
              // makeAspectCrop or centerCrop.
              unit: '%',
              width: 90,
            },
            16 / 9,
            width,
            height
          ),
          width,
          height
        )
        imageRef.current=e.currentTarget
        setCrop(crop)
      }
    const {t} = useTranslation()
    return <Dialog
        dir={'ltr'}
        open={isOpen}
        onClose={handleCancel}
        aria-labelledby="alert-dialog-title2"
        aria-describedby="alert-dialog-description2"
    >
        <DialogTitle id="alert-dialog-title2">{t('edit-picture')}</DialogTitle>
        <DialogContent>
            <DialogContentText id="alert-dialog-description2">
                <ReactCrop
                    src={src}
                    crop={crop}
                    aspect={16/9}
                    ruleOfThirds
                    onChange={(crop,precentCrop)=>setCrop(precentCrop)}
                    onComplete={(c) => setCompletedCrop(c)}
                    // Mobile-specific optimizations
                    touchDragEnabled={true}
                    dragSelection={true}
                    showSelection={true}
                    // Ensure high quality rendering
                    style={{
                        maxWidth: '100%',
                        height: 'auto'
                    }}
                >
                    <img 
                        src={src} 
                        onLoad={onImageLoad}
                        style={{
                            maxWidth: '100%',
                            height: 'auto',
                            display: 'block'
                        }}
                        alt="Crop preview"
                    />
                </ReactCrop>
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={handleCancel} color="primary" variant={"outlined"}>
                {t('doesnt-matter')}
            </Button>
            <Button onClick={async (e) => {
                const croppedImage = await getCroppedImg(imageRef.current, completedCrop, uuidv4())
                handleApprove(e, croppedImage);
            }} color="primary" variant={"contained"} autoFocus>
                {t('i-finished')}
            </Button>
        </DialogActions>
    </Dialog>
}

export default CropImages;